import BaseChart from 'components/BaseChart/BaseChart';
import { createChartOption } from 'components/BaseChart/utils/chartUtils';
import useImageObjectUrlCache from 'features/integrations/hooks/useImageObjectUrlCache';
import { selectHistoryDataLoading } from 'features/integrations/state/selectors/integrationSelectors';
import { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import {
  buildAwareDeviceChartModel,
  buildAwareTooltipFormatter,
  getAwareDevices,
  getSeriesType,
  getYAxisLabel,
} from '../utils/deviceGraphUtils.js';

const AwareDeviceChart = ({
  title = 'Devices',
  selectedDeviceMap,
  selectedDataType,
  unitType,
  setUnitType, // still here if you add toggle UI later
  startTime,
  endTime,
  onSelectDeviceIndex,
}) => {
  const chartRef = useRef(null);
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const historyDataLoading = useSelector(selectHistoryDataLoading);

  const { labels, datasets, markLineData } = useMemo(
    () =>
      buildAwareDeviceChartModel({
        devices: connectedDevices,
        selectedDeviceMap,
        selectedDataType,
        unitType,
        startTime,
        endTime,
      }),
    [connectedDevices, selectedDeviceMap, selectedDataType, unitType, startTime, endTime],
  );

  const hasData =
    Array.isArray(labels) && labels.length > 0 && Array.isArray(datasets) && datasets.length > 0;

  const seriesType = getSeriesType(selectedDataType);
  const yAxisLabel = getYAxisLabel(selectedDataType, unitType);

  const alignedDatasets = useMemo(() => {
    if (!hasData) return [];

    // Adds a map of timestamp to raw data for quick lookup in tooltip
    return datasets.map((dataset) => {
      const rawPoints = dataset?._meta?.data || []; // JavaScript object with device object combined with its data
      const byTimestampMap = new Map(rawPoints.map((point) => [point.ts, point])); // creates a map of timestamp to raw data

      return {
        ...dataset,
        _meta: { ...(dataset._meta || {}), byTimestampMap },
      };
    });
  }, [hasData, datasets]);

  const imageUrls = useMemo(() => {
    if (!hasData) return [];

    const urls = new Set();
    datasets.forEach((ds) => {
      ds?._meta?.data?.forEach((point) => {
        if (point?.img_link_url) urls.add(point.img_link_url);
      });
    });
    return [...urls];
  }, [hasData, datasets]);

  const imageMap = useImageObjectUrlCache(imageUrls);

  // Creates the echart series data
  const series = useMemo(() => {
    if (!hasData) return [];

    return alignedDatasets.map((dataset) => ({
      name: dataset.label,
      type: seriesType,
      data: dataset.data,
      // hide per-point symbols; only show on hover via emphasis
      showSymbol: false,
      connectNulls: true,
      smooth: false,
      itemStyle: {
        color: dataset.color,
      },
      lineStyle: {
        width: 2,
      },
    }));
  }, [hasData, alignedDatasets, seriesType]);

  const tooltipFormatter = useMemo(
    () => (hasData ? buildAwareTooltipFormatter(alignedDatasets, imageMap, unitType) : null),
    [hasData, alignedDatasets, imageMap, unitType],
  );

  const xAxisLabelFormatter = useMemo(() => {
    const formatter = new Intl.DateTimeFormat([], {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return (value) => formatter.format(new Date(value)).replace(', ', ' ');
  }, []);

  const option = useMemo(() => {
    if (!hasData) return null;
    return createChartOption({
      title: title,
      titleTop: 8,
      xAxisLabels: labels,
      minValue: startTime,
      maxValue: endTime,
      series,
      gridPadding: {
        left: '10%',
        right: '2%',
        top: '26%',
        bottom: '18%',
      },
      tooltipFormatter: tooltipFormatter || undefined,
      xAxisRotate: 0,
      xAxisLabelFontSize: 10,
      xAxisLabelHideOverlap: true,
      xAxisLabelInterval: 'auto',
      xAxisLabelType: 'time',
      xAxisLabelFormatter,
      dataZoomEnabled: true, // set true if you want a zoom slider
      markLineData,
      yAxisLabel: yAxisLabel,
    });
  }, [
    title,
    hasData,
    labels,
    series,
    tooltipFormatter,
    markLineData,
    yAxisLabel,
    startTime,
    endTime,
    xAxisLabelFormatter,
  ]);

  const handleResetZoom = () => {
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;
    instance.dispatchAction({ type: 'restore' });
    instance.dispatchAction({ type: 'dataZoom', start: 0, end: 100 });
  };

  const handleToggleUnits = () => {
    if (typeof setUnitType === 'function') {
      setUnitType((prev) => !prev);
    }
  };

  const onEvents = useMemo(() => {
    return {
      click: (params) => {
        if (!onSelectDeviceIndex) return;
        if (params.componentType !== 'series') return;

        const seriesIndex = params.seriesIndex ?? 0;
        const dataset = alignedDatasets[seriesIndex] || datasets[seriesIndex] || {};
        const targetId = dataset?._meta?.id;

        if (!targetId) return;

        // Find the matching device in the global devices array so DeviceContext selection works
        const globalIndex = (connectedDevices || []).findIndex((d) => d.id === targetId);

        if (globalIndex >= 0) {
          onSelectDeviceIndex(globalIndex);
          return;
        }

        // Fallback to aware-only ordering
        const sortedDevices = (getAwareDevices(connectedDevices) || []).sort((a, b) => {
          const systemTypeComparison = a.system_type.localeCompare(b.system_type);
          if (systemTypeComparison !== 0) return systemTypeComparison;

          const deviceTypeComparison = a.device_type.localeCompare(b.device_type);
          if (deviceTypeComparison !== 0) return deviceTypeComparison;

          const aKey = a.id;
          const bKey = b.id;
          return parseInt(aKey, 10) - parseInt(bKey, 10);
        });

        const awareIndex = sortedDevices.findIndex((node) => node.id === targetId);

        if (awareIndex >= 0) {
          onSelectDeviceIndex(awareIndex);
        }
      },
    };
  }, [alignedDatasets, datasets, connectedDevices, onSelectDeviceIndex]);

  // No-chart fallback
  if (!hasData) {
    // If historical data is still loading, show a loading message instead of "No device selected"
    if (historyDataLoading) {
      return (
        <div className="bg-primary h-100 rounded-3 overflow-hidden d-flex flex-column justify-content-center align-items-center text-white">
          <div className="spinner-border spinner-border-sm mb-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="fs-5">Loading device data...</div>
        </div>
      );
    }
    return (
      <div className="bg-primary h-100 rounded-3 overflow-hidden d-flex justify-content-center align-items-center fs-4 text-white">
        No device selected
      </div>
    );
  }

  if (!option) return null;

  return (
    <BaseChart
      ref={chartRef}
      option={option}
      onEvents={onEvents}
      notMerge={true}
      autoResize
      height="100%"
      width="100%"
      controlsLeft={
        <Button type="button" size="sm" className="m-2 px-2 py-1" onClick={handleToggleUnits}>
          Units: {unitType ? 'Metric (m)' : 'Imperial (ft)'}
        </Button>
      }
      controlsRight={
        <Button type="button" size="sm" className="m-1 px-2 py-1" onClick={handleResetZoom}>
          Reset Zoom
        </Button>
      }
    />
  );
};

export default AwareDeviceChart;
